/*
 * What tells two projects apart at a glance: a colour and an initial, both derived from the project's id
 * and name so every surface (decision breadcrumb, Inbox filter, Projects page) shows the same marker.
 */

/** Calm, distinguishable hues; the project id picks one, so a project keeps its colour forever. */
const HUES = [212, 152, 28, 286, 346, 182, 48, 252] as const;

export function projectHue(projectId: string): number {
  let h = 0;
  for (let i = 0; i < projectId.length; i++) h = (h * 31 + projectId.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length]!;
}

export const projectColor = (projectId: string): string => `hsl(${projectHue(projectId)} 62% 46%)`;

export const projectInitial = (name: string): string => (name.trim()[0] ?? "?").toUpperCase();

export type CrumbProject = { id: string; name: string };

/** Keep the decisions of one project; a null/empty filter keeps everything. */
export function filterByProject<T extends { projectId: string }>(list: readonly T[], projectId: string | null): T[] {
  return projectId ? list.filter((d) => d.projectId === projectId) : [...list];
}

/** "All" first, then each project that exists in the office (zero counts included), with how many decisions wait in it. */
export function projectCounts(list: readonly { projectId: string }[], projects: readonly CrumbProject[]): { id: string | null; name: string; count: number }[] {
  return [{ id: null, name: "All", count: list.length }, ...projects.map((p) => ({ id: p.id, name: p.name, count: list.filter((d) => d.projectId === p.id).length }))];
}

/** The `?project=` value: kept while projects load (so a reload doesn't flash the unfiltered list), ignored once it is known to name no project. */
export const validProjectFilter = (value: string | null, projects: readonly CrumbProject[] | undefined): string | null =>
  value && (!projects || projects.some((p) => p.id === value)) ? value : null;

/** Decisions in `now` whose ids were not in `seen`: what a browser notification should announce. */
export const newDecisions = <T extends { id: string }>(seen: ReadonlySet<string>, now: readonly T[]): T[] => now.filter((d) => !seen.has(d.id));

/** A browser notification fires only with permission, and never while the tab is already in front of the user. */
export const shouldNotify = (permission: string | undefined, visibility: string | undefined): boolean => permission === "granted" && visibility !== "visible";

/** "Employee001: Review #1214 ..." for Roomie's bubble. The name is omitted only while the project is unknown. */
export const withProject = (projectName: string | undefined, text: string): string => (projectName ? `${projectName}: ${text}` : text);
